/**
 * PTORequest.js - PTO Request Page
 * 
 * Allows teachers to:
 * - Submit new PTO requests (multiple dates)
 * - View their existing PTO requests
 * - Cancel pending requests
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useIsMobile } from '../hooks/useIsMobile';
import dataStore from '../data/dataStore';

function PTORequest() {
  const { user } = useAuth();
  const { theme, colors, darkMode } = useTheme();
  const isMobile = useIsMobile();
  
  const [selectedDates, setSelectedDates] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [reason, setReason] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  const [allApprovedPTO, setAllApprovedPTO] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // Partial day PTO state
  const [isPartialDay, setIsPartialDay] = useState(false);
  const [partialStartTime, setPartialStartTime] = useState('08:00');
  const [partialHours, setPartialHours] = useState(4);

  // Load user's PTO requests and all approved PTOs
  const loadRequests = () => {
    if (user) {
      setMyRequests(dataStore.getPTORequestsByUser(user.id));
    }
    // Load all approved PTOs for the team view
    const approved = dataStore.getPTORequestsByStatus('Approved');
    setAllApprovedPTO(approved);
  };

  useEffect(() => {
    loadRequests();
  }, [user]);

  // Add a date to selection (with validation)
  const toggleDate = (dateStr) => {
    // Check if it's a valid date
    const dateObj = new Date(dateStr + 'T12:00:00');
    if (isNaN(dateObj.getTime())) return;
    
    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dateObj < today) return;
    
    // Toggle selection
    if (selectedDates.includes(dateStr)) {
      setSelectedDates(selectedDates.filter(d => d !== dateStr));
    } else {
      setSelectedDates([...selectedDates, dateStr].sort());
    }
  };

  // Calendar navigation
  const prevMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
  };

  // Get calendar data for current month
  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const startingDay = firstDay.getDay(); // 0 = Sunday
    
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    // Build calendar grid
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push(dateStr);
    }
    
    return days;
  };

  // Check if a date is in the past
  const isPastDate = (dateStr) => {
    const dateObj = new Date(dateStr + 'T12:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dateObj < today;
  };

  // Check if date is today
  const isToday = (dateStr) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return dateStr === todayStr;
  };

  // Check if date is within 2 weeks (too soon for PTO request) - only applies to non-admin users
  const isTooSoon = (dateStr) => {
    // Admins and Super Admins can request PTO for any future date
    if (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') return false;
    
    const dateObj = new Date(dateStr + 'T12:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const twoWeeksFromNow = new Date(today);
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 15); // 15 days to ensure 14 full future days
    return dateObj >= today && dateObj < twoWeeksFromNow;
  };

  // Check if date already has a PTO request (Pending or Approved) for current user
  // Returns: null if no PTO, or { isPartial, status, startTime, hours } if PTO exists
  const getExistingPTO = (dateStr) => {
    const existing = myRequests.find(req => 
      (req.status === 'Pending' || req.status === 'Approved') && 
      req.dates.includes(dateStr)
    );
    if (!existing) return null;
    return {
      isPartial: existing.isPartialDay || false,
      status: existing.status,
      startTime: existing.partialStartTime,
      hours: existing.partialHours
    };
  };

  // Simple check if date has any existing PTO (for backwards compatibility)
  const hasExistingPTO = (dateStr) => {
    return myRequests.some(req => 
      (req.status === 'Pending' || req.status === 'Approved') && 
      req.dates.includes(dateStr)
    );
  };

  // Check if date has FULL day PTO (blocks the whole day)
  const hasFullDayPTO = (dateStr) => {
    return myRequests.some(req => 
      (req.status === 'Pending' || req.status === 'Approved') && 
      req.dates.includes(dateStr) &&
      !req.isPartialDay
    );
  };

  // Format a date string from YYYY-MM-DD to MM/DD/YYYY
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  };

  // Format an array of dates, grouping consecutive dates into ranges
  const formatDateRanges = (dates) => {
    if (!dates || dates.length === 0) return '-';
    
    // Sort dates first
    const sortedDates = [...dates].sort();
    
    // Group consecutive dates into ranges
    const ranges = [];
    let rangeStart = sortedDates[0];
    let rangeEnd = sortedDates[0];
    
    for (let i = 1; i < sortedDates.length; i++) {
      const currentDate = new Date(sortedDates[i] + 'T12:00:00');
      const prevDate = new Date(sortedDates[i - 1] + 'T12:00:00');
      
      // Check if current date is exactly 1 day after previous
      const diffDays = (currentDate - prevDate) / (1000 * 60 * 60 * 24);
      
      if (diffDays === 1) {
        // Extend the current range
        rangeEnd = sortedDates[i];
      } else {
        // Save the previous range and start a new one
        ranges.push({ start: rangeStart, end: rangeEnd });
        rangeStart = sortedDates[i];
        rangeEnd = sortedDates[i];
      }
    }
    
    // Don't forget the last range
    ranges.push({ start: rangeStart, end: rangeEnd });
    
    // Format ranges as strings
    return ranges.map(range => {
      if (range.start === range.end) {
        return formatDate(range.start);
      } else {
        return `${formatDate(range.start)} - ${formatDate(range.end)}`;
      }
    }).join(', ');
  };

  // Remove a date from selection
  const removeDate = (dateStr) => {
    setSelectedDates(selectedDates.filter(d => d !== dateStr));
  };

  // Group approved PTOs by teacher with merged consecutive dates (filtered by selected calendar month)
  const getApprovedPTOGrouped = () => {
    const selectedYear = calendarMonth.getFullYear();
    const selectedMonth = calendarMonth.getMonth();
    
    // Group by teacher
    const byTeacher = {};
    
    allApprovedPTO.forEach(request => {
      const teacher = dataStore.getUserById(request.userId);
      const teacherName = teacher?.name || 'Unknown';
      
      request.dates.forEach(dateStr => {
        const dateObj = new Date(dateStr + 'T12:00:00');
        // Only include dates within the selected calendar month
        if (dateObj.getFullYear() === selectedYear && dateObj.getMonth() === selectedMonth) {
          if (!byTeacher[teacherName]) {
            byTeacher[teacherName] = [];
          }
          byTeacher[teacherName].push({
            date: dateStr,
            isPartialDay: request.isPartialDay,
            partialStartTime: request.partialStartTime,
            partialHours: request.partialHours
          });
        }
      });
    });
    
    // For each teacher, merge consecutive dates into ranges
    const result = [];
    
    Object.entries(byTeacher).forEach(([teacherName, entries]) => {
      // Sort entries by date
      entries.sort((a, b) => a.date.localeCompare(b.date));
      
      // Group consecutive dates (only merge if same partial day status)
      let currentRange = null;
      
      entries.forEach(entry => {
        if (!currentRange) {
          currentRange = { 
            startDate: entry.date, 
            endDate: entry.date, 
            teacherName,
            isPartialDay: entry.isPartialDay,
            partialStartTime: entry.partialStartTime,
            partialHours: entry.partialHours
          };
        } else {
          // Check if this date is consecutive to the current range end
          const prevDate = new Date(currentRange.endDate + 'T12:00:00');
          const currDate = new Date(entry.date + 'T12:00:00');
          const diffDays = (currDate - prevDate) / (1000 * 60 * 60 * 24);
          
          // Only merge if consecutive AND same partial day status
          if (diffDays === 1 && entry.isPartialDay === currentRange.isPartialDay) {
            currentRange.endDate = entry.date;
          } else {
            // Save current range and start new one
            result.push(currentRange);
            currentRange = { 
              startDate: entry.date, 
              endDate: entry.date, 
              teacherName,
              isPartialDay: entry.isPartialDay,
              partialStartTime: entry.partialStartTime,
              partialHours: entry.partialHours
            };
          }
        }
      });
      
      // Don't forget the last range
      if (currentRange) {
        result.push(currentRange);
      }
    });
    
    // Sort by start date
    result.sort((a, b) => a.startDate.localeCompare(b.startDate));
    
    return result;
  };

  // Check if a date is already covered by an existing PTO request (Pending or Approved)
  const getOverlappingDates = (dates) => {
    const overlapping = [];
    dates.forEach(date => {
      const existingRequest = myRequests.find(req => 
        (req.status === 'Pending' || req.status === 'Approved') && 
        req.dates.includes(date)
      );
      if (existingRequest) {
        overlapping.push({
          date: date,
          status: existingRequest.status
        });
      }
    });
    return overlapping;
  };

  // Submit PTO request
  const handleSubmit = async () => {
    setMessage('');
    setError('');

    if (selectedDates.length === 0) {
      setError('Please select at least one date');
      return;
    }

    // Check for 2-week advance notice requirement (only for non-admin users)
    if (user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const twoWeeksFromNow = new Date(today);
      twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 15); // 15 days to ensure 14 full future days
      
      const tooSoonDates = selectedDates.filter(dateStr => {
        const requestDate = new Date(dateStr + 'T12:00:00');
        return requestDate < twoWeeksFromNow;
      });
      
      if (tooSoonDates.length > 0) {
        const formattedDates = tooSoonDates.map(d => formatDate(d)).join(', ');
        const twoWeeksDateStr = twoWeeksFromNow.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
        setError(`PTO requests must be submitted at least 2 weeks in advance. The following dates are too soon: ${formattedDates}. Please select dates on or after ${twoWeeksDateStr}.`);
        return;
      }
    }

    // Check for overlapping dates with existing requests
    const overlapping = getOverlappingDates(selectedDates);
    if (overlapping.length > 0) {
      const dateList = overlapping.map(o => 
        `${formatDate(o.date)} (${o.status})`
      ).join(', ');
      setError(`You already have PTO requests for: ${dateList}`);
      return;
    }

    try {
      const partialDayInfo = isPartialDay ? {
        isPartial: true,
        startTime: partialStartTime,
        hours: partialHours
      } : null;
      
      await dataStore.createPTORequest(user.id, selectedDates, reason, partialDayInfo);
      setSelectedDates([]);
      setReason('');
      setIsPartialDay(false);
      setPartialStartTime('08:00');
      setPartialHours(4);
      loadRequests();
      setMessage('PTO request submitted successfully!');
    } catch (err) {
      console.error('Error submitting PTO request:', err);
      setError(err.message);
    }
  };

  // Cancel a pending request
  const handleCancel = async (requestId) => {
    try {
      await dataStore.cancelPTORequest(requestId, user.id);
      loadRequests();
      setMessage('PTO request cancelled');
    } catch (err) {
      console.error('Error cancelling PTO request:', err);
      setError(err.message);
    }
  };

  // Styles
  const containerStyle = {
    padding: isMobile ? '12px' : '20px',
    maxWidth: '1400px',
    margin: '0 auto'
  };

  const cardStyle = {
    backgroundColor: theme.cardBackground,
    padding: isMobile ? '16px' : '25px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    marginBottom: isMobile ? '12px' : '20px'
  };

  const titleStyle = {
    color: theme.text,
    marginTop: 0,
    marginBottom: isMobile ? '12px' : '20px',
    fontSize: isMobile ? '1.2rem' : '1.5rem'
  };

  const inputStyle = {
    padding: isMobile ? '14px' : '12px',
    border: `1px solid ${theme.border}`,
    borderRadius: '4px',
    backgroundColor: theme.background,
    color: theme.text,
    marginRight: isMobile ? '0' : '10px',
    fontSize: isMobile ? '16px' : 'inherit' // Prevents iOS zoom
  };

  const buttonStyle = {
    padding: isMobile ? '14px 20px' : '12px 20px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    color: darkMode ? '#fff' : '#000'
  };

  const dateTagStyle = {
    display: 'inline-block',
    padding: '6px 12px',
    margin: '4px',
    borderRadius: '4px',
    backgroundColor: colors.blue,
    color: darkMode ? '#fff' : '#000'
  };

  const removeTagStyle = {
    marginLeft: '8px',
    cursor: 'pointer',
    fontWeight: 'bold'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'auto'
  };

  const thStyle = {
    padding: '12px',
    backgroundColor: colors.green,
    color: darkMode ? '#1a1a2e' : '#fff',
    textAlign: 'left',
    fontWeight: '600',
    whiteSpace: 'nowrap'
  };

  const tdStyle = {
    padding: '12px',
    borderBottom: `1px solid ${theme.border}`,
    color: theme.text,
    whiteSpace: 'nowrap'
  };

  const getStatusStyle = (status) => ({
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: status === 'Approved' ? colors.green :
                     status === 'Denied' ? colors.red : colors.blue,
    color: (status === 'Approved' || status === 'Denied') && darkMode ? '#1a1a2e' : '#fff',
    fontSize: '0.85rem',
    fontWeight: '600'
  });

  const messageStyle = {
    padding: '10px',
    borderRadius: '4px',
    marginBottom: '15px',
    textAlign: 'center'
  };

  return (
    <div style={containerStyle}>
      {/* New Request Card */}
      <div style={cardStyle}>
        <h2 style={titleStyle}>🏖️ Request Time Off</h2>

        {message && (
          <div style={{ ...messageStyle, backgroundColor: colors.green, color: darkMode ? '#1a1a2e' : '#fff', fontWeight: '600' }}>
            {message}
          </div>
        )}
        {error && (
          <div style={{ ...messageStyle, backgroundColor: colors.red, color: darkMode ? '#1a1a2e' : '#fff', fontWeight: '600' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '16px' : '30px', flexWrap: 'wrap' }}>
          {/* Left Side - Calendar */}
          <div style={{ flex: '0 0 auto', width: isMobile ? '100%' : 'auto' }}>
            <label style={{ color: theme.text, display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              Select Date(s):
            </label>
            
            {/* Custom Calendar */}
            <div style={{
              backgroundColor: theme.backgroundSecondary,
              borderRadius: '8px',
              padding: isMobile ? '12px' : '15px',
              width: isMobile ? '100%' : '320px',
              boxSizing: 'border-box'
            }}>
            {/* Month Navigation */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <button
                type="button"
                onClick={prevMonth}
                style={{
                  padding: '8px 12px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '4px',
                  backgroundColor: theme.cardBackground,
                  color: theme.text,
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                ‹
              </button>
              <span style={{ color: theme.text, fontWeight: 'bold', fontSize: '1.1rem' }}>
                {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <button
                type="button"
                onClick={nextMonth}
                style={{
                  padding: '8px 12px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '4px',
                  backgroundColor: theme.cardBackground,
                  color: theme.text,
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                ›
              </button>
            </div>

            {/* Day Headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '4px',
              marginBottom: '8px'
            }}>
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} style={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  color: theme.textSecondary,
                  padding: '4px'
                }}>
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '4px'
            }}>
              {getCalendarDays().map((dateStr, index) => {
                if (!dateStr) {
                  return <div key={`empty-${index}`} style={{ padding: '8px' }} />;
                }

                const isSelected = selectedDates.includes(dateStr);
                const isPast = isPastDate(dateStr);
                const isTodayDate = isToday(dateStr);
                const tooSoon = isTooSoon(dateStr);
                const existingPTO = getExistingPTO(dateStr);
                const hasPTO = !!existingPTO;
                const hasPartialPTO = existingPTO?.isPartial || false;
                const hasFullPTO = hasPTO && !hasPartialPTO;
                const dayNum = parseInt(dateStr.split('-')[2]);
                // Disable for past dates, full-day PTO, or dates within 2 weeks
                const isDisabled = isPast || hasFullPTO || tooSoon;

                return (
                  <button
                    key={dateStr}
                    type="button"
                    onClick={() => !isDisabled && toggleDate(dateStr)}
                    disabled={isDisabled}
                    title={hasFullPTO ? 'You have full-day PTO for this date' : 
                           hasPartialPTO ? `Partial PTO: ${existingPTO.startTime} (${existingPTO.hours}h) - Click to select for additional time off` :
                           tooSoon ? 'PTO must be requested at least 2 weeks in advance' : ''}
                    style={{
                      padding: '8px',
                      border: isTodayDate ? `2px solid ${colors.blue}` : 'none',
                      borderRadius: '4px',
                      backgroundColor: isSelected 
                        ? colors.blue 
                        : hasFullPTO
                          ? colors.green
                          : (isPast || tooSoon)
                            ? 'transparent' 
                            : theme.cardBackground,
                      color: isSelected 
                        ? '#fff' 
                        : hasFullPTO
                          ? (darkMode ? '#1a1a2e' : '#fff')
                          : (isPast || tooSoon)
                            ? theme.textSecondary 
                            : theme.text,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: isSelected || isTodayDate || hasPTO ? 'bold' : 'normal',
                      opacity: isPast ? 0.4 : (tooSoon ? 0.5 : 1),
                      transition: 'all 0.15s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Partial day triangle indicator */}
                    {hasPartialPTO && !isSelected && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 0,
                        height: 0,
                        borderStyle: 'solid',
                        borderWidth: '0 24px 24px 0',
                        borderColor: `transparent ${colors.green} transparent transparent`
                      }} />
                    )}
                    {dayNum}
                  </button>
                );
              })}
            </div>

            {/* 2-week notice reminder - only show for non-admin users */}
            {user?.role !== 'ADMIN' && user?.role !== 'SUPER_ADMIN' && (
              <div style={{
                marginTop: '12px',
                padding: '8px 12px',
                backgroundColor: darkMode ? 'rgba(255,167,38,0.15)' : 'rgba(255,167,38,0.1)',
                borderRadius: '6px',
                borderLeft: `3px solid ${colors.orange}`,
                fontSize: '0.8rem',
                color: theme.textSecondary
              }}>
                ⚠️ PTO requests must be submitted at least <strong style={{ color: theme.text }}>2 weeks</strong> in advance. Grayed-out dates within the next 2 weeks are unavailable.
              </div>
            )}

            {/* Clear Selection Button */}
            {selectedDates.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedDates([])}
                style={{
                  marginTop: '12px',
                  padding: '6px 12px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: theme.textSecondary,
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  width: '100%'
                }}
              >
                Clear All Selections
              </button>
            )}

            {/* Calendar Legend */}
            <div style={{ 
              marginTop: '12px', 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: '12px', 
              fontSize: '0.75rem',
              color: theme.textSecondary
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '12px', height: '12px', backgroundColor: colors.blue, borderRadius: '2px' }}></div>
                <span>Selected</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '12px', height: '12px', backgroundColor: colors.green, borderRadius: '2px' }}></div>
                <span>Full Day PTO</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ 
                  width: '12px', 
                  height: '12px', 
                  backgroundColor: theme.cardBackground, 
                  borderRadius: '2px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 0,
                    height: 0,
                    borderStyle: 'solid',
                    borderWidth: '0 12px 12px 0',
                    borderColor: `transparent ${colors.green} transparent transparent`
                  }} />
                </div>
                <span>Partial PTO</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '12px', height: '12px', border: `2px solid ${colors.blue}`, borderRadius: '2px', boxSizing: 'border-box' }}></div>
                <span>Today</span>
              </div>
            </div>
          </div>
        </div>

          {/* Right Side - Selected Dates, Reason, Submit */}
          <div style={{ flex: '1', minWidth: isMobile ? '100%' : '250px', width: isMobile ? '100%' : 'auto' }}>
            {/* Selected Dates */}
            <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
              <strong style={{ color: theme.text, display: 'block', marginBottom: '10px' }}>Selected Dates:</strong>
              <div style={{ 
                minHeight: '60px',
                padding: '12px',
                backgroundColor: theme.backgroundSecondary,
                borderRadius: '8px',
                border: `1px solid ${theme.border}`
              }}>
                {selectedDates.length === 0 ? (
                  <span style={{ color: theme.textSecondary }}>No dates selected</span>
                ) : (
                  selectedDates.map(date => (
                    <span key={date} style={dateTagStyle}>
                      {formatDate(date)}
                      <span style={removeTagStyle} onClick={() => removeDate(date)}>×</span>
                    </span>
                  ))
                )}
              </div>
            </div>

            {/* Reason (optional) */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: theme.text, display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Reason (optional):
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Vacation, Doctor appointment..."
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            {/* Partial Day Toggle */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: theme.text, 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}>
                <input
                  type="checkbox"
                  checked={isPartialDay}
                  onChange={(e) => setIsPartialDay(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                Partial Day (hourly PTO)
              </label>
              
              {isPartialDay && (
                <div style={{ 
                  marginTop: '12px', 
                  padding: '15px',
                  backgroundColor: theme.backgroundSecondary,
                  borderRadius: '8px',
                  border: `1px solid ${theme.border}`
                }}>
                  <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                    {/* Start Time */}
                    <div style={{ flex: '1', minWidth: '120px' }}>
                      <label style={{ color: theme.text, display: 'block', marginBottom: '6px', fontSize: '0.9rem' }}>
                        Start Time:
                      </label>
                      <select
                        value={partialStartTime}
                        onChange={(e) => setPartialStartTime(e.target.value)}
                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                      >
                        <option value="07:00">7:00 AM</option>
                        <option value="07:30">7:30 AM</option>
                        <option value="08:00">8:00 AM</option>
                        <option value="08:30">8:30 AM</option>
                        <option value="09:00">9:00 AM</option>
                        <option value="09:30">9:30 AM</option>
                        <option value="10:00">10:00 AM</option>
                        <option value="10:30">10:30 AM</option>
                        <option value="11:00">11:00 AM</option>
                        <option value="11:30">11:30 AM</option>
                        <option value="12:00">12:00 PM</option>
                        <option value="12:30">12:30 PM</option>
                        <option value="13:00">1:00 PM</option>
                        <option value="13:30">1:30 PM</option>
                        <option value="14:00">2:00 PM</option>
                        <option value="14:30">2:30 PM</option>
                        <option value="15:00">3:00 PM</option>
                        <option value="15:30">3:30 PM</option>
                        <option value="16:00">4:00 PM</option>
                      </select>
                    </div>
                    
                    {/* Hours */}
                    <div style={{ flex: '1', minWidth: '120px' }}>
                      <label style={{ color: theme.text, display: 'block', marginBottom: '6px', fontSize: '0.9rem' }}>
                        Duration:
                      </label>
                      <select
                        value={partialHours}
                        onChange={(e) => setPartialHours(parseInt(e.target.value))}
                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                      >
                        <option value={1}>1 hour</option>
                        <option value={2}>2 hours</option>
                        <option value={3}>3 hours</option>
                        <option value={4}>4 hours</option>
                        <option value={5}>5 hours</option>
                        <option value={6}>6 hours</option>
                        <option value={7}>7 hours</option>
                        <option value={8}>8 hours</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Preview */}
                  <div style={{ 
                    marginTop: '10px', 
                    padding: '8px 12px',
                    backgroundColor: colors.blue + '20',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: theme.text
                  }}>
                    ⏰ PTO: {(() => {
                      const [h, m] = partialStartTime.split(':').map(Number);
                      const startAmPm = h >= 12 ? 'PM' : 'AM';
                      const startH12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                      const endH = h + partialHours;
                      const endAmPm = endH >= 12 ? 'PM' : 'AM';
                      const endH12 = endH > 12 ? endH - 12 : (endH === 0 ? 12 : endH);
                      return `${startH12}:${String(m).padStart(2, '0')} ${startAmPm} - ${endH12}:${String(m).padStart(2, '0')} ${endAmPm}`;
                    })()}
                  </div>
                </div>
              )}
            </div>

            <button
              style={{ ...buttonStyle, backgroundColor: colors.green, width: '100%', color: darkMode ? '#1a1a2e' : '#fff', fontWeight: '600' }}
              onClick={handleSubmit}
            >
              Submit Request
            </button>
          </div>
        </div>
      </div>

      {/* Two-column layout for Team PTO and My Requests */}
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row', 
        gap: isMobile ? '12px' : '20px' 
      }}>
        {/* Team Approved PTO Card */}
        <div style={{ ...cardStyle, flex: isMobile ? 'none' : '0 0 380px', marginBottom: 0 }}>
          <h2 style={titleStyle}>👥 Team PTO</h2>
          <p style={{ color: theme.textSecondary, marginTop: '-10px', marginBottom: '15px', fontSize: '0.9rem' }}>
            {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
          
          {(() => {
            const approvedGrouped = getApprovedPTOGrouped();
            
            if (approvedGrouped.length === 0) {
              return <p style={{ color: theme.textSecondary }}>No approved PTO this month.</p>;
            }
            
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {approvedGrouped.map((entry, idx) => (
                  <div 
                    key={idx}
                    style={{
                      padding: '10px 12px',
                      backgroundColor: theme.backgroundSecondary,
                      borderRadius: '8px',
                      borderLeft: `4px solid ${colors.green}`,
                      fontSize: '0.9rem',
                      color: theme.text,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={{ fontWeight: '600' }}>
                      {entry.startDate === entry.endDate 
                        ? formatDate(entry.startDate)
                        : `${formatDate(entry.startDate)} - ${formatDate(entry.endDate)}`
                      }
                    </span>
                    <span>{entry.teacherName}</span>
                    {entry.isPartialDay && (
                      <span style={{ 
                        fontSize: '0.75rem', 
                        color: theme.textSecondary,
                        backgroundColor: theme.hover,
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>
                        {(() => {
                          const [h, m] = entry.partialStartTime.split(':').map(Number);
                          const amPm = h >= 12 ? 'PM' : 'AM';
                          const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                          return `${h12}:${String(m).padStart(2, '0')} ${amPm}`;
                        })()} ({entry.partialHours}h)
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* My Requests Card */}
        <div style={{ ...cardStyle, flex: isMobile ? 'none' : '1', marginBottom: 0 }}>
          <h2 style={titleStyle}>📋 My PTO Requests</h2>

        {myRequests.length === 0 ? (
          <p style={{ color: theme.textSecondary }}>No PTO requests yet.</p>
        ) : isMobile ? (
          /* Mobile: Card-based layout */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {myRequests.map(req => (
              <div 
                key={req.id}
                style={{
                  padding: '14px',
                  backgroundColor: theme.backgroundSecondary,
                  borderRadius: '8px',
                  borderLeft: `4px solid ${
                    req.status === 'Approved' ? colors.green :
                    req.status === 'Denied' ? colors.red : colors.blue
                  }`
                }}
              >
                {/* Status Badge */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '10px'
                }}>
                  <span style={getStatusStyle(req.status)}>{req.status}</span>
                  {req.status === 'Pending' && (
                    <button
                      style={{ 
                        ...buttonStyle, 
                        backgroundColor: colors.red, 
                        padding: '8px 14px', 
                        color: darkMode ? '#1a1a2e' : '#fff', 
                        fontWeight: '600',
                        fontSize: '0.85rem'
                      }}
                      onClick={() => handleCancel(req.id)}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                
                {/* Dates */}
                <div style={{ marginBottom: '6px' }}>
                  <span style={{ fontWeight: '600', color: theme.text }}>
                    {formatDateRanges(req.dates)}
                  </span>
                </div>
                
                {/* Type */}
                <div style={{ fontSize: '0.85rem', color: theme.textSecondary, marginBottom: '4px' }}>
                  {req.isPartialDay ? (
                    <span>
                      ⏰ Partial: {(() => {
                        const [h, m] = req.partialStartTime.split(':').map(Number);
                        const amPm = h >= 12 ? 'PM' : 'AM';
                        const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                        return `${h12}:${String(m).padStart(2, '0')} ${amPm}`;
                      })()} ({req.partialHours}h)
                    </span>
                  ) : (
                    <span>Full Day</span>
                  )}
                </div>
                
                {/* Reason */}
                {req.reason && (
                  <div style={{ fontSize: '0.85rem', color: theme.textSecondary, fontStyle: 'italic' }}>
                    "{req.reason}"
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* Desktop: Table layout */
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Dates</th>
                <th style={thStyle}>Type</th>
                <th style={{ ...thStyle, whiteSpace: 'normal' }}>Reason</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {myRequests.map(req => (
                <tr key={req.id}>
                  <td style={tdStyle}>{formatDateRanges(req.dates)}</td>
                  <td style={tdStyle}>
                    {req.isPartialDay ? (
                      <span style={{ fontSize: '0.85rem' }}>
                        ⏰ {(() => {
                          const [h, m] = req.partialStartTime.split(':').map(Number);
                          const amPm = h >= 12 ? 'PM' : 'AM';
                          const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                          return `${h12}:${String(m).padStart(2, '0')} ${amPm}`;
                        })()} ({req.partialHours}h)
                      </span>
                    ) : (
                      <span style={{ fontSize: '0.85rem' }}>Full Day</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: 'normal', maxWidth: '350px' }}>{req.reason || '-'}</td>
                  <td style={tdStyle}>
                    <span style={getStatusStyle(req.status)}>{req.status}</span>
                  </td>
                  <td style={tdStyle}>
                    {req.status === 'Pending' && (
                      <button
                        style={{ ...buttonStyle, backgroundColor: colors.red, padding: '6px 12px', color: darkMode ? '#1a1a2e' : '#fff', fontWeight: '600' }}
                        onClick={() => handleCancel(req.id)}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </div>
    </div>
  );
}

export default PTORequest;
